import { useEffect, type ReactNode } from 'react';
import type { Rol } from '@medchoice/shared';
import { irA, resolver, useRuta } from './router';
import { ROUTES, type Route } from './routes';
import { ProveedorSesion, useSesion } from './session/Sesion';
import { ProveedorAvisos } from './ui/Avisos';
import { Boton } from './ui/Boton';
import { Cargando, EstadoVacio } from './ui/Estados';
import { LimiteDeError } from './ui/layout/LimiteDeError';
import { MENU_ADMINISTRACION, MENU_PROFESIONAL } from './ui/layout/menus';
import { PaginaAcceso } from './ui/layout/Marco';
import { PaginaPanel } from './ui/layout/PaginaPanel';
import { PaginaPublica } from './ui/layout/PaginaPublica';

const TIPO_DE_CUENTA: Record<Rol, string> = {
  paciente: 'de paciente',
  profesional: 'de profesional',
  administrador: 'de administración',
};

function conDiseno(ruta: Route, pantalla: ReactNode) {
  switch (ruta.layout) {
    case 'publico':
      return <PaginaPublica sinRelleno={ruta.fullWidth}>{pantalla}</PaginaPublica>;
    case 'acceso':
      return <PaginaAcceso>{pantalla}</PaginaAcceso>;
    case 'panel-profesional':
      return <PaginaPanel menu={MENU_PROFESIONAL} inicio="/pro/agenda">{pantalla}</PaginaPanel>;
    case 'panel-admin':
      return <PaginaPanel menu={MENU_ADMINISTRACION} inicio="/admin" distintivo="Admin">{pantalla}</PaginaPanel>;
  }
}

function NoEncontrada() {
  return (
    <PaginaPublica>
      <EstadoVacio
        icono="compass"
        titulo="No encontramos esta página"
        cuerpo="El enlace puede estar mal escrito o la sección ya no existe."
        accion={<Boton onClick={() => { irA('/buscar'); }}>Volver al buscador</Boton>}
      />
    </PaginaPublica>
  );
}

function OtroRol({ requeridos, actual, ruta }: { requeridos: Rol[]; actual: Rol; ruta: string }) {
  const [principal = actual] = requeridos;
  return (
    <PaginaPublica>
      <EstadoVacio
        icono="lock"
        titulo={`Esta sección es para una cuenta ${TIPO_DE_CUENTA[principal]}`}
        cuerpo={`Estás con una sesión ${TIPO_DE_CUENTA[actual]}. Entrá con la cuenta que corresponde para seguir.`}
        accion={
          <Boton onClick={() => { irA(`/login?volverA=${encodeURIComponent(ruta)}`); }}>
            Ir al ingreso
          </Boton>
        }
      />
    </PaginaPublica>
  );
}

function Contenido() {
  const { ruta, consulta } = useRuta();
  const { usuario, cargando } = useSesion();

  const encontrada = resolver(ROUTES, ruta);
  const titulo = encontrada?.definicion.title;
  useEffect(() => { document.title = titulo ? `${titulo} · MedChoice` : 'MedChoice'; }, [titulo]);
  const roles = encontrada?.definicion.roles;
  const faltaSesion = !cargando && roles !== undefined && !usuario;

  // Quien llega sin sesión a una pantalla que la necesita va al ingreso llevando a
  // dónde quería ir; después de entrar vuelve exactamente ahí.
  useEffect(() => {
    if (faltaSesion) irA(`/login?volverA=${encodeURIComponent(ruta)}`, { reemplazar: true });
  }, [ruta, faltaSesion]);

  if (cargando) {
    return <div className="pagina-cargando"><Cargando texto="Preparando MedChoice" /></div>;
  }
  if (faltaSesion) return null;
  if (!encontrada) return <NoEncontrada />;

  const { definicion, params } = encontrada;

  // Con sesión abierta pero de otro rol no se redirige: echar a alguien que ya
  // entró confunde. Se le explica y se le ofrece la salida.
  if (roles && usuario && !roles.includes(usuario.rol)) {
    return <OtroRol requeridos={roles} actual={usuario.rol} ruta={ruta} />;
  }

  const Pantalla = definicion.screen;
  // La clave cambia con la pantalla y sus parámetros, no con la consulta: filtrar
  // resultados no es cambiar de pantalla y no tiene que volver a animar la entrada.
  const clave = `${definicion.path}:${Object.values(params).join('/')}`;
  return conDiseno(definicion, (
    <div key={clave} className="entrada-pantalla">
      <Pantalla params={params} consulta={consulta} />
    </div>
  ));
}

export function App() {
  const { ruta } = useRuta();
  return (
    <ProveedorAvisos>
      <ProveedorSesion>
        <LimiteDeError ruta={ruta}>
          <Contenido />
        </LimiteDeError>
      </ProveedorSesion>
    </ProveedorAvisos>
  );
}
